import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

/**
 * Home Page
 * Landing page with project information and call-to-action
 */
const HomePage = () => {
  const navigate = useNavigate();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(145deg, #5b6b8c 0%, #6b5c88 55%, #4a5a78 100%)',
          color: 'white',
          py: { xs: 6, md: 10 },
          textAlign: 'center',
          boxShadow: 'inset 0 -1px 0 rgba(255,255,255,0.08)',
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: 3,
              mb: 2,
              bgcolor: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <SecurityIcon sx={{ fontSize: { xs: 56, sm: 80 } }} />
          </Box>
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
            SafeSocial
          </Typography>
          <Typography variant="h5" gutterBottom sx={{ opacity: 0.95, fontWeight: 500 }}>
            Social Platform Exposure Tracker
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, mb: 4, maxWidth: 520, mx: 'auto', opacity: 0.92 }}>
            Protect your privacy by discovering what personal information you've
            accidentally shared on social media platforms.
          </Typography>
          <Button
            variant="contained"
            size="large"
            color="secondary"
            onClick={() => navigate('/login')}
            sx={{ px: 4, py: 1.5 }}
          >
            Get Started
          </Button>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700 }}>
          How It Works
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
          SafeSocial helps you identify and protect your exposed personal data
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(42, 49, 66, 0.07)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(42, 49, 66, 0.1)' },
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <SearchIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  1. Connect & Scan
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Securely connect your Reddit account using OAuth.
                  We never store your password.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(42, 49, 66, 0.07)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(42, 49, 66, 0.1)' },
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <WarningIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  2. Detect Exposures
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Our AI scans your posts and comments for exposed emails,
                  phone numbers, and addresses.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(42, 49, 66, 0.07)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(42, 49, 66, 0.1)' },
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  3. Take Action
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Get personalized recommendations and AI-powered guidance
                  to secure your data.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Privacy Section */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, rgba(238,241,246,0.95) 0%, rgba(255,255,255,0.6) 100%)',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" align="center" gutterBottom>
            Your Privacy is Our Priority
          </Typography>
          <Stack spacing={2} sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <CheckCircleIcon color="success" sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="h6" gutterBottom>
                  OAuth Authentication
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We use secure OAuth 2.0. You log in directly with the social
                  platform - we never see or store your password.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <CheckCircleIcon color="success" sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="h6" gutterBottom>
                  Secure Data Storage
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All your data is encrypted and stored securely in MongoDB Atlas
                  cloud database.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <CheckCircleIcon color="success" sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="h6" gutterBottom>
                  You Control Your Data
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Delete your data anytime. We provide complete transparency
                  about what we collect and how we use it.
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Ready to Protect Your Privacy?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Start scanning your social media accounts for exposed personal information
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/login')}
          sx={{ px: 4, py: 1.5 }}
        >
          Get Started Now
        </Button>
      </Container>
    </Box>
  );
};

export default HomePage;
